import React, { useState } from 'react';

const API_BASE_URL = '';

interface FormData {
  name: string;
  email: string;
  organization: string;
  subject: string;
  message: string;
}

const initialFormData: FormData = {
  name: '',
  email: '',
  organization: '',
  subject: '',
  message: '',
};

export default function Contact() {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Unable to send your message. Please try again.');
      }

      setSubmitted(true);
      setFormData(initialFormData);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Unable to send your message. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-tealLight/30 to-paper text-ink antialiased">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        {/* Page Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-tealLight border border-teal/20 text-teal text-xs font-semibold uppercase tracking-wider">
              Get in Touch
            </div>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight text-teal">
            Contact Us
          </h1>

          <p className="mt-6 text-lg sm:text-xl text-inkSoft max-w-3xl mx-auto leading-relaxed">
            We welcome conversations with healthcare providers, researchers,
            life sciences organizations, MedTech companies, and potential
            collaborators.
          </p>
        </div>

        {/* Contact Layout */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Information */}
          <div>
            <div className="bg-white border border-rule rounded-xl p-8 shadow-[4px_4px_0_0_rgba(20,184,166,0.18)]">
              <h2 className="text-2xl font-bold text-ink mb-8">
                Contact Information
              </h2>

              <div className="space-y-8">
                {/* Location */}
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-11 h-11 rounded-lg bg-tealLight flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-teal"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 21.9a1.5 1.5 0 01-2.828 0l-4.243-5.243A8 8 0 1117.657 16.657z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  </div>

                  <div>
                    <h3 className="font-semibold text-ink mb-1">Location</h3>
                    <p className="text-inkSoft">Global</p>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-11 h-11 rounded-lg bg-tealLight flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-teal"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </div>

                  <div>
                    <h3 className="font-semibold text-ink mb-1">Email</h3>
                    <a
                      href="mailto:ramsamudralaindous@gmail.com"
                      className="text-teal hover:text-teal/80 transition-colors break-all"
                    >
                      ramsamudralaindous@gmail.com
                    </a>
                  </div>
                </div>

                {/* LinkedIn */}
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-11 h-11 rounded-lg bg-tealLight flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-teal"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M19 3a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h14zm-.5 15.5v-5.4c0-2.9-1.5-4.3-3.6-4.3-1.7 0-2.5.9-2.9 1.5v-1.3H8.4v9.5H12v-5.3c0-1.4.3-2.4 1.7-2.4 1.3 0 1.3 1.3 1.3 2.4v5.3h3.5zM6.9 7.7c1.1 0 1.8-.7 1.8-1.7s-.7-1.7-1.8-1.7-1.8.7-1.8 1.7.7 1.7 1.8 1.7zM5.3 18.5h3.5V9H5.3v9.5z" />
                    </svg>
                  </div>

                  <div>
                    <h3 className="font-semibold text-ink mb-1">
                      LinkedIn
                    </h3>
                    <a
                      href="https://www.linkedin.com/company/diagnostic-intelligence/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-teal hover:text-teal/80 transition-colors"
                    >
                      Diagnostic Intelligence
                    </a>
                  </div>
                </div>

                {/* Support */}
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-11 h-11 rounded-lg bg-tealLight flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-teal"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>

                  <div>
                    <h3 className="font-semibold text-ink mb-1">
                      Support Hours
                    </h3>
                    <p className="text-inkSoft">
                      24/7 - AI-powered support
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Demo */}
            <div className="mt-8 bg-teal text-white rounded-xl p-8 shadow-[4px_4px_0_0_rgba(20,184,166,0.25)]">
              <h2 className="text-2xl font-bold mb-3">
                Explore Diagnostic Intelligence
              </h2>

              <p className="text-white/90 leading-relaxed mb-6">
                Experience the platform through our interactive demonstration
                and explore how multimodal disease intelligence is being
                developed.
              </p>

              <a
                href="https://hetu-intelligence.xyz/interactive-demo"
                className="inline-flex items-center justify-center bg-white text-teal font-bold px-6 py-3 rounded-lg hover:bg-white/90 transition-colors"
              >
                Interactive Demo →
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white border border-rule rounded-xl p-8 shadow-[4px_4px_0_0_rgba(20,184,166,0.18)]">
            <h2 className="text-2xl font-bold text-ink mb-2">
              Send Us a Message
            </h2>

            <p className="text-inkSoft mb-8">
              Tell us about your organization, research interests,
              collaboration opportunity, or questions about Diagnostic
              Intelligence.
            </p>

            {submitted ? (
              <div className="rounded-xl bg-tealLight border border-teal/20 p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-teal text-white flex items-center justify-center">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>

                  <div>
                    <h3 className="font-bold text-ink text-lg mb-1">
                      Message Sent
                    </h3>
                    <p className="text-inkSoft">
                      Thank you for contacting Diagnostic Intelligence. We
                      will review your message and respond as appropriate.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name */}
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-semibold text-ink mb-2"
                  >
                    Name *
                  </label>

                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-rule bg-paper text-ink placeholder-inkSoft/60 focus:outline-none focus:ring-2 focus:ring-teal/30 focus:border-teal transition-colors"
                    placeholder="Your name"
                  />
                </div>

                {/* Email */}
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-semibold text-ink mb-2"
                  >
                    Email *
                  </label>

                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-rule bg-paper text-ink placeholder-inkSoft/60 focus:outline-none focus:ring-2 focus:ring-teal/30 focus:border-teal transition-colors"
                    placeholder="you@example.com"
                  />
                </div>

                {/* Organization */}
                <div>
                  <label
                    htmlFor="organization"
                    className="block text-sm font-semibold text-ink mb-2"
                  >
                    Organization
                  </label>

                  <input
                    id="organization"
                    name="organization"
                    type="text"
                    value={formData.organization}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-rule bg-paper text-ink placeholder-inkSoft/60 focus:outline-none focus:ring-2 focus:ring-teal/30 focus:border-teal transition-colors"
                    placeholder="Organization or institution"
                  />
                </div>

                {/* Subject */}
                <div>
                  <label
                    htmlFor="subject"
                    className="block text-sm font-semibold text-ink mb-2"
                  >
                    Subject *
                  </label>

                  <input
                    id="subject"
                    name="subject"
                    type="text"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-rule bg-paper text-ink placeholder-inkSoft/60 focus:outline-none focus:ring-2 focus:ring-teal/30 focus:border-teal transition-colors"
                    placeholder="How can we help?"
                  />
                </div>

                {/* Message */}
                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-semibold text-ink mb-2"
                  >
                    Message *
                  </label>

                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-rule bg-paper text-ink placeholder-inkSoft/60 focus:outline-none focus:ring-2 focus:ring-teal/30 focus:border-teal transition-colors resize-y"
                    placeholder="Tell us how we can help..."
                  />
                </div>

                {/* Error */}
                {error && (
                  <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {error}
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full inline-flex items-center justify-center bg-teal text-white font-bold px-8 py-3.5 rounded-lg shadow-md hover:shadow-lg hover:bg-teal/90 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message →'}
                </button>

                <p className="text-xs text-inkSoft text-center">
                  By submitting this form, you agree that Diagnostic
                  Intelligence may use the information provided to respond to
                  your inquiry.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
